import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  User,
  CreditCard,
  FileText,
  Banknote,
  Calendar,
  Save,
  Loader2,
  RefreshCw,
  Zap,
  Check,
  Printer,
  Download,
  X,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_TERMS = ["CASH", "COD", "7 Days", "14 Days", "30 Days", "60 Days", "90 Days"];

// Profile Tab Component
const ProfileTab = ({ supplier, onSave, saving, onReset, onClearUnallocated, onRefresh }) => {
  const [formData, setFormData] = useState(supplier || {});
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [addingPayment, setAddingPayment] = useState(false);
  const [resetOptions, setResetOptions] = useState({
    clear_journals: true,
    clear_payments: true,
    clear_grvs: false,
    new_opening_balance: ''
  });
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setFormData(supplier || {});
  }, [supplier]);

  const handleChange = (field, value) => {
    // Auto-sync is_cod with payment_terms so the supplier list badge always
    // matches the chosen term (no stale "COD" badge after the user switches
    // to "30 Days" or similar).
    if (field === "payment_terms") {
      setFormData(prev => ({ ...prev, payment_terms: value, is_cod: value === "COD" }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Belt-and-braces normalisation: force is_cod to match payment_terms so
    // legacy out-of-sync values get corrected on every save.
    onSave({ ...formData, is_cod: formData.payment_terms === "COD" });
  };

  const handleAddUnallocatedPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setAddingPayment(true);
    // One idempotency key survives every retry — the backend upserts on
    // this key so a proxy 504/524 retry can never insert a duplicate row.
    const idempotency_key = (crypto?.randomUUID?.() || `pmt-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    try {
      const response = await saveWithRetry(() => axios.post(`${API}/suppliers/${supplier.id}/payments`, {
        amount: amount,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: "EFT",
        reference: paymentReference || "Historical payment - reconciliation",
        note: "One-time adjustment: Payment recorded but not allocated to specific invoices",
        auto_allocate: false,
        allocations: [],
        idempotency_key
      }));
      
      toast.success(`Payment of R${amount.toFixed(2)} added successfully (Ref: ${response.data.payment_ref})`);
      setShowAddPaymentDialog(false);
      setPaymentAmount('');
      setPaymentReference('');
      onRefresh?.();
    } catch (error) {
      console.error("Error adding payment:", error);
      toast.error(errorMessage(error, "Failed to add payment"));
    } finally {
      setAddingPayment(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const payload = {
        clear_journals: resetOptions.clear_journals,
        clear_payments: resetOptions.clear_payments,
        clear_grvs: resetOptions.clear_grvs
      };
      if (resetOptions.new_opening_balance !== '') {
        payload.new_opening_balance = parseFloat(resetOptions.new_opening_balance) || 0;
      }
      
      const response = await axios.post(`${API}/suppliers/${supplier.id}/reset-financial`, payload);
      toast.success(`Reset complete: ${response.data.actions_taken.join(', ')}`);
      setShowResetDialog(false);
      onReset?.();
    } catch (error) {
      console.error("Error resetting:", error);
      toast.error(error.response?.data?.detail || "Failed to reset financial data");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Aging Summary at top */}
      <div className="grid grid-cols-7 gap-2 text-center text-sm bg-gray-50 p-3 rounded-lg">
        {/* Not Yet Due - only show for statement-based (30 Days) suppliers */}
        {supplier?.payment_terms === '30 Days' && (
          <div className="p-2 bg-gray-100 rounded">
            <div className="text-xs text-gray-600">Not Yet Due</div>
            <div className="font-bold text-gray-700">R {(supplier?.aging?.not_yet_due || 0).toFixed(2)}</div>
          </div>
        )}
        <div className="p-2 bg-green-100 rounded">
          <div className="text-xs text-green-600">{supplier?.payment_terms === '30 Days' ? 'Payable This Month' : 'Current'}</div>
          <div className="font-bold text-green-700">R {(supplier?.aging?.current || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-yellow-100 rounded">
          <div className="text-xs text-yellow-600">30 Days</div>
          <div className="font-bold">R {(supplier?.aging?.days_30 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-orange-100 rounded">
          <div className="text-xs text-orange-600">60 Days</div>
          <div className="font-bold">R {(supplier?.aging?.days_60 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-red-50 rounded">
          <div className="text-xs text-red-500">90 Days</div>
          <div className="font-bold">R {(supplier?.aging?.days_90 || 0).toFixed(2)}</div>
        </div>
        {supplier?.payment_terms !== '30 Days' && (
          <div className="p-2 bg-red-100 rounded">
            <div className="text-xs text-red-600">120+ Days</div>
            <div className="font-bold text-red-700">R {(supplier?.aging?.days_120 || supplier?.aging?.over_120 || 0).toFixed(2)}</div>
          </div>
        )}
        <div className="p-2 bg-blue-100 rounded border-2 border-blue-300">
          <div className="text-xs text-blue-600">Balance</div>
          <div className="font-bold text-blue-700">R {(supplier?.balance_owing || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Key Info Row */}
      <div className="grid grid-cols-5 gap-4 p-3 bg-blue-50 rounded-lg">
        <div>
          <Label className="text-xs text-gray-500">My Account No</Label>
          <Input 
            value={formData.account_number || ''} 
            onChange={(e) => handleChange('account_number', e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Credit Limit</Label>
          <Input 
            type="number"
            value={formData.credit_limit || ''} 
            onChange={(e) => handleChange('credit_limit', parseFloat(e.target.value) || 0)}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500 flex items-center gap-1">
            Opening Balance
            <span className="text-orange-500" title="Amount owed before system start">*</span>
          </Label>
          <Input 
            type="number"
            step="0.01"
            value={formData.opening_balance || ''} 
            onChange={(e) => handleChange('opening_balance', parseFloat(e.target.value) || 0)}
            className="mt-1 border-orange-300"
            placeholder="0.00"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Last Purchase</Label>
          <div className="mt-1 p-2 bg-white rounded border text-sm">
            {supplier?.last_purchase_date || 'N/A'}
          </div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Unallocated Payments</Label>
          <div className="mt-1 p-2 bg-green-100 rounded border border-green-300 text-sm font-bold text-green-700 flex justify-between items-center">
            <span>R {(supplier?.unallocated_payments || 0).toFixed(2)}</span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                onClick={() => setShowAddPaymentDialog(true)}
              >
                + Add
              </Button>
              {(supplier?.unallocated_payments || 0) > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-100"
                  onClick={() => onClearUnallocated && onClearUnallocated()}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Unallocated Payment Dialog */}
      {showAddPaymentDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[400px] shadow-xl">
            <h3 className="text-lg font-bold mb-4">Add Unallocated Payment</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a payment that will be available to allocate to invoices later. 
              Use this for historical payments that were not recorded in the system.
            </p>
            <div className="space-y-4">
              <div>
                <Label>Amount (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="e.g. 20802.53"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Reference / Note</Label>
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g. Historical payments - March 2026"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddPaymentDialog(false);
                  setPaymentAmount('');
                  setPaymentReference('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddUnallocatedPayment}
                disabled={addingPayment || !paymentAmount}
              >
                {addingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Add Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Details */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <Label>Supplier Name *</Label>
            <Input 
              value={formData.name || ''} 
              onChange={(e) => handleChange('name', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Contact Person</Label>
            <Input 
              value={formData.contact_person || ''} 
              onChange={(e) => handleChange('contact_person', e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Phone</Label>
              <Input 
                value={formData.phone || ''} 
                onChange={(e) => handleChange('phone', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input 
                value={formData.alt_phone || ''} 
                onChange={(e) => handleChange('alt_phone', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input 
              type="email"
              value={formData.email || ''} 
              onChange={(e) => handleChange('email', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Website</Label>
            <Input 
              value={formData.website || ''} 
              onChange={(e) => handleChange('website', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div>
            <Label>VAT Number</Label>
            <Input 
              value={formData.vat_number || ''} 
              onChange={(e) => handleChange('vat_number', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Street Address</Label>
            <Input 
              value={formData.address || ''} 
              onChange={(e) => handleChange('address', e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>City</Label>
              <Input 
                value={formData.city || ''} 
                onChange={(e) => handleChange('city', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Postal Code</Label>
              <Input 
                value={formData.postal_code || ''} 
                onChange={(e) => handleChange('postal_code', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Select value={formData.payment_terms || '30 Days'} onValueChange={(v) => handleChange('payment_terms', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...PAYMENT_TERMS].sort().map(term => (
                  <SelectItem key={term} value={term}>{term}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Consignment Supplier Checkbox */}
          <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <input
              type="checkbox"
              id="is_consignment"
              checked={formData.is_consignment || false}
              onChange={(e) => handleChange('is_consignment', e.target.checked)}
              className="h-4 w-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
            />
            <Label htmlFor="is_consignment" className="text-sm text-purple-700 cursor-pointer">
              Consignment Supplier
            </Label>
            <span className="text-xs text-purple-500 ml-2">
              (Stock owned by supplier until sold - excluded from Audit Pack reports)
            </span>
          </div>

          {/* Cash Supplier Checkbox */}
          <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <input
              type="checkbox"
              id="is_cash"
              checked={formData.is_cash || false}
              onChange={(e) => handleChange('is_cash', e.target.checked)}
              className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
              data-testid="supplier-is-cash-checkbox"
            />
            <Label htmlFor="is_cash" className="text-sm text-emerald-700 cursor-pointer">
              Cash Supplier
            </Label>
            <span className="text-xs text-emerald-600 ml-2">
              (Paid with cash on hand — counted in Sales Allocation Cash vs Sales metric)
            </span>
          </div>
          
          {/* Minimum Order Value Section */}
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
            <Label className="text-sm font-semibold text-orange-700">Minimum Order Requirements</Label>

            {/* Weight supplier toggle */}
            <div className="flex items-center gap-2 p-2 bg-white rounded border border-orange-200">
              <input
                type="checkbox"
                id="is_weight_supplier"
                checked={formData.is_weight_supplier || false}
                onChange={(e) => handleChange('is_weight_supplier', e.target.checked)}
                className="h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                data-testid="supplier-is-weight-checkbox"
              />
              <Label htmlFor="is_weight_supplier" className="text-sm cursor-pointer">
                Weight-based supplier (minimum order is in <strong>KG</strong>, not Rand)
              </Label>
            </div>

            {formData.is_weight_supplier ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Minimum Order Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.minimum_order_weight_kg || ''}
                    onChange={(e) => handleChange('minimum_order_weight_kg', parseFloat(e.target.value) || 0)}
                    className="mt-1"
                    placeholder="e.g., 1000"
                    data-testid="supplier-min-weight-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Minimum Type</Label>
                  <Select
                    value={formData.minimum_order_type || 'per_order'}
                    onValueChange={(v) => handleChange('minimum_order_type', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_order">Per Order (each order must meet minimum)</SelectItem>
                      <SelectItem value="combined">Combined (multiple orders can add up)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Minimum Order Value (R)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.minimum_order_value || ''}
                    onChange={(e) => handleChange('minimum_order_value', parseFloat(e.target.value) || 0)}
                    className="mt-1"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label className="text-xs">Minimum Type</Label>
                  <Select
                    value={formData.minimum_order_type || 'per_order'}
                    onValueChange={(v) => handleChange('minimum_order_type', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_order">Per Order (each order must meet minimum)</SelectItem>
                      <SelectItem value="combined">Combined (multiple orders can add up)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <p className="text-xs text-orange-600">
              {formData.minimum_order_type === 'combined'
                ? "Multiple orders within a period can combine to meet the minimum"
                : "Each individual order must meet the minimum"}
              {formData.is_weight_supplier && " — total weight is calculated from each product's Weight (kg) field."}
            </p>
          </div>
          
          <div>
            <Label>Notes</Label>
            <Textarea 
              value={formData.notes || ''} 
              onChange={(e) => handleChange('notes', e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-between pt-4 border-t">
        <Button 
          variant="outline" 
          onClick={() => setShowResetDialog(true)} 
          className="text-red-600 border-red-300 hover:bg-red-50"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset Financial Data
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Reset Financial Data Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reset Financial Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              This will clear the selected financial data for <strong>{supplier?.name}</strong>. 
              This action cannot be undone.
            </p>
            
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={resetOptions.clear_journals}
                  onChange={(e) => setResetOptions(prev => ({...prev, clear_journals: e.target.checked}))}
                  className="rounded"
                />
                <span>Clear all journal entries (DEBIT, CREDIT, PAYMENT, etc.)</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={resetOptions.clear_payments}
                  onChange={(e) => setResetOptions(prev => ({...prev, clear_payments: e.target.checked}))}
                  className="rounded"
                />
                <span>Clear all payment records & allocations</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={resetOptions.clear_grvs}
                  onChange={(e) => setResetOptions(prev => ({...prev, clear_grvs: e.target.checked}))}
                  className="rounded"
                />
                <span className="text-red-600">Clear all GRVs (DANGER: affects stock!)</span>
              </label>
            </div>
            
            <div className="pt-2 border-t">
              <Label>Set New Opening Balance (optional)</Label>
              <Input 
                type="number"
                step="0.01"
                placeholder="Leave empty to keep current"
                value={resetOptions.new_opening_balance}
                onChange={(e) => setResetOptions(prev => ({...prev, new_opening_balance: e.target.value}))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current opening balance: R {(supplier?.opening_balance || 0).toFixed(2)}
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReset} 
                disabled={resetting}
                className="bg-red-600 hover:bg-red-700"
              >
                {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Reset Data
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Transactions Tab Component
const TransactionsTab = ({ supplier, supplierId }) => {
  const [transactions, setTransactions] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchTransactions();
  }, [supplierId, dateRange]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/transactions`, {
        params: { start_date: dateRange.start, end_date: dateRange.end }
      });
      setTransactions(response.data?.transactions || []);
      // Use the period carry-forward (B/Forward at filter start) — NOT the
      // supplier's lifetime opening balance. Otherwise transactions posted
      // before the date filter window are silently dropped from the running
      // total and the closing balance no longer matches the card.
      const summary = response.data?.summary || {};
      setOpeningBalance(
        summary.period_opening_balance != null
          ? summary.period_opening_balance
          : (summary.opening_balance || 0)
      );
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  // Build the statement rows: oldest → newest with running balance, then reverse for display.
  // Filters out:
  //   • the synthetic "Opening" row (we render our own B/Forward at the bottom).
  //   • zero-value "clutter" rows — Manual reconciliation PAYMENT entries and
  //     any other document with BOTH debit=0 AND credit=0. They don't move
  //     balance and just add noise; user asked for a clean debit/credit view.
  const buildRows = () => {
    const realTxns = transactions.filter(t => {
      if (t.doc_type === "OPENING") return false;
      const d = Number(t.debit || 0);
      const c = Number(t.credit || 0);
      // Drop pure-zero rows so the statement shows only real debits/credits.
      if (d === 0 && c === 0) return false;
      return true;
    });
    const sortedAsc = [...realTxns].sort((a, b) => {
      const cmp = (a.date || '').localeCompare(b.date || '');
      if (cmp !== 0) return cmp;
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
    let running = openingBalance || 0;
    const withBalance = sortedAsc.map(t => {
      running = running + (t.debit || 0) - (t.credit || 0);
      return {
        ...t,
        running_balance: Math.round(running * 100) / 100,
        type_label: typeLabelFor(t),
        reference_label: referenceLabelFor(t),
      };
    });
    return withBalance.reverse(); // newest first for display
  };

  const typeLabelFor = (t) => {
    if (t.doc_type === "COD") return "COD";
    if (t.doc_type === "OPENING") return "B/Forward";
    // GRV-sourced invoice (non-COD) — flag it explicitly as an account
    // purchase so the operator can tell at a glance which invoices were
    // GRV'd on account vs COD, without opening the GRV.
    if (t.doc_type === "INVOICE" || (t.grv_number && t.debit > 0)) return "Account Invoice";
    if (t.debit > 0 && t.credit === 0) return "Debit Journal";
    if (t.credit > 0 && t.debit === 0) return "Credit Journal";
    return t.doc_type || "Entry";
  };

  const referenceLabelFor = (t) => {
    // Prefer the supplier-provided invoice number / payment reference;
    // fall back to the document number, then a trimmed description.
    if (t.reference) return t.reference;
    if (t.doc_no && t.doc_no !== '-') return t.doc_no;
    if (t.description) {
      // Strip the "GRV GRV-XXX - " prefix to leave just the supplier-side label.
      const stripped = t.description.replace(/^GRV\s+GRV-\d+\s*-\s*/i, "").trim();
      return stripped || t.description;
    }
    return "—";
  };

  const rows = buildRows();
  const closingBalance = rows.length > 0 ? rows[0].running_balance : (openingBalance || 0);
  const totalDebit = rows.reduce((s, r) => s + (r.debit || 0), 0);
  const totalCredit = rows.reduce((s, r) => s + (r.credit || 0), 0);

  const handlePrint = () => {
    // Build a fully self-contained statement HTML and append it as the only
    // printable element. Same pattern as ShelfTalkerModal — avoids CSS-isolation
    // issues with modals / sticky headers / position:fixed UI bleeding through.
    const PRINT_HOST_ID = "bh-supplier-statement-print-host";
    const PRINT_STYLE_ID = "bh-supplier-statement-print-style";
    document.getElementById(PRINT_HOST_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();

    const esc = (s) => String(s ?? "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

    const badgeStyleFor = (label) => {
      if (label === "Debit Journal") return "color:#b91c1c;background:#fee2e2;border:1px solid #fecaca";
      if (label === "Credit Journal") return "color:#047857;background:#d1fae5;border:1px solid #a7f3d0";
      if (label === "COD") return "color:#92400e;background:#fef3c7;border:1px solid #fde68a";
      if (label === "Account Invoice") return "color:#1e40af;background:#dbeafe;border:1px solid #bfdbfe";
      if (label === "B/Forward") return "color:#374151;background:#e5e7eb";
      return "color:#1d4ed8;background:#dbeafe;border:1px solid #bfdbfe";
    };

    const noteFor = (t) => {
      const d = (t.description || "").replace(/^GRV\s+GRV-\d+\s*-\s*/i, "").trim();
      if (!d) return "";
      if (d === t.reference_label) return "";
      return d;
    };

    const rowsHtml = rows.map(t => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-family:monospace;white-space:nowrap;font-size:10pt">${esc(formatRowDate(t.date))}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:10pt">
          <div>${esc(t.reference_label)}</div>
          ${noteFor(t) ? `<div style="font-size:8.5pt;color:#6b7280;margin-top:2px;line-height:1.2">${esc(noteFor(t))}</div>` : ""}
        </td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb"><span style="font-size:9pt;padding:1px 6px;border-radius:3px;${badgeStyleFor(t.type_label)}">${esc(t.type_label)}</span></td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace;font-size:10pt">${t.debit > 0 ? `R ${t.debit.toFixed(2)}` : '—'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace;font-size:10pt;color:#047857">${t.credit > 0 ? `-R ${t.credit.toFixed(2)}` : '—'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace;font-size:10pt;font-weight:600;${t.running_balance < 0 ? 'color:#047857' : ''}">R ${t.running_balance.toFixed(2)}</td>
      </tr>`).join("");

    const bfRow = `
      <tr style="background:#f9fafb">
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-family:monospace;white-space:nowrap;font-size:10pt">${esc(formatRowDate(dateRange.start))}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-style:italic;color:#6b7280;font-size:10pt">Brought forward</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb"><span style="font-size:9pt;padding:1px 6px;border-radius:3px;${badgeStyleFor("B/Forward")}">B/Forward</span></td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:10pt">—</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-size:10pt">—</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-family:monospace;font-weight:600;font-size:10pt;${(openingBalance || 0) < 0 ? 'color:#047857' : ''}">R ${(openingBalance || 0).toFixed(2)}</td>
      </tr>`;

    const closingColor = closingBalance < 0 ? "color:#047857" : "color:#111827";

    const html = `
      <div style="font-family:Arial,sans-serif;color:#111827;max-width:190mm;margin:0 auto">
        <h1 style="margin:0 0 4px;font-size:20pt;text-align:center">Statement of Account</h1>
        <div style="text-align:center;font-size:14pt;font-weight:600">${esc(supplier?.name || '')}</div>
        <div style="text-align:center;font-size:10pt;color:#6b7280;margin:4px 0 14px">
          Account #: ${esc(supplier?.supplier_number || supplier?.account_number || '—')} &nbsp;|&nbsp;
          Terms: ${esc(supplier?.payment_terms || '—')} &nbsp;|&nbsp;
          Period: ${esc(dateRange.start)} → ${esc(dateRange.end)} &nbsp;|&nbsp;
          Generated: ${new Date().toLocaleDateString('en-GB')}
        </div>
        <table style="width:100%;border-collapse:collapse;border:1px solid #d1d5db">
          <thead>
            <tr style="background:#f3f4f6">
              <th style="padding:8px;border-bottom:2px solid #d1d5db;text-align:left;width:24mm;font-size:10pt">Date</th>
              <th style="padding:8px;border-bottom:2px solid #d1d5db;text-align:left;font-size:10pt">Reference</th>
              <th style="padding:8px;border-bottom:2px solid #d1d5db;text-align:left;width:30mm;font-size:10pt">Type</th>
              <th style="padding:8px;border-bottom:2px solid #d1d5db;text-align:right;width:25mm;font-size:10pt">Debit</th>
              <th style="padding:8px;border-bottom:2px solid #d1d5db;text-align:right;width:25mm;font-size:10pt">Credit</th>
              <th style="padding:8px;border-bottom:2px solid #d1d5db;text-align:right;width:28mm;font-size:10pt">Balance</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}${bfRow}</tbody>
        </table>
        <table style="width:100%;margin-top:10mm;font-size:10pt">
          <tr>
            <td style="color:#6b7280;width:50%">Total Debit</td>
            <td style="text-align:right;font-family:monospace;font-weight:600">R ${totalDebit.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="color:#6b7280">Total Credit</td>
            <td style="text-align:right;font-family:monospace;font-weight:600;color:#047857">-R ${totalCredit.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="color:#6b7280;border-top:2px solid #d1d5db;padding-top:6px"><strong>Closing Balance</strong></td>
            <td style="text-align:right;font-family:monospace;font-weight:700;font-size:13pt;${closingColor};border-top:2px solid #d1d5db;padding-top:6px">R ${closingBalance.toFixed(2)}</td>
          </tr>
        </table>
      </div>`;

    const host = document.createElement("div");
    host.id = PRINT_HOST_ID;
    host.innerHTML = html;
    document.body.appendChild(host);

    const style = document.createElement("style");
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      #${PRINT_HOST_ID} { display: none; }
      @media print {
        @page { size: A4; margin: 12mm 10mm; }
        /* Belt-and-braces isolation — see ShelfTalkerModal for the same pattern. */
        html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
        body * { visibility: hidden !important; }
        body > *:not(#${PRINT_HOST_ID}) { display: none !important; }
        #emergent-badge,
        [id="emergent-badge"],
        [class*="emergent-badge"] { display: none !important; visibility: hidden !important; }
        #${PRINT_HOST_ID},
        #${PRINT_HOST_ID} * { visibility: visible !important; }
        /* No position:fixed — would clip the second+ pages of a long statement. */
        #${PRINT_HOST_ID} {
          display: block !important;
          position: static !important;
          background: #fff !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `;
    document.head.appendChild(style);

    const cleanup = () => {
      try { host.remove(); } catch (_) {}
      try { style.remove(); } catch (_) {}
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);

    setTimeout(() => {
      window.print();
      setTimeout(cleanup, 4000);
    }, 60);
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/suppliers/${supplierId}/statement/pdf`, {
        params: { start_date: dateRange.start, end_date: dateRange.end },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const safe = (supplier?.name || "supplier").replace(/[^A-Za-z0-9._-]+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement_${safe}_${dateRange.start || "all"}_to_${dateRange.end || "today"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate PDF");
    }
  };

  const handleEmail = async () => {
    if (!supplier?.email) {
      toast.error(`${supplier?.name || 'Supplier'} has no email address on file.`);
      return;
    }    setEmailing(true);
    try {
      const res = await axios.post(`${API}/suppliers/${supplierId}/email-statement`, {
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      if (res.data?.status === "success") {
        toast.success(`Statement emailed to ${supplier.email}`);
      } else if (res.data?.status === "blocked") {
        toast.warning("Email blocked — emails are disabled in Settings.");
      } else {
        toast.warning(res.data?.message || "Statement sent, but email service returned no confirmation.");
      }
    } catch (e) {
      console.error("Email statement failed:", e);
      toast.error(e.response?.data?.detail || "Failed to email statement");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      {/* Date filter + Print/Email toolbar */}
      <div className="flex flex-wrap gap-4 items-end justify-between no-print">
        <div className="flex gap-4 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-40"
              data-testid="supplier-txn-date-from"
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-40"
              data-testid="supplier-txn-date-to"
            />
          </div>
          <Button variant="outline" onClick={fetchTransactions} size="sm">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="supplier-statement-print-btn">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="supplier-statement-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEmail}
            disabled={emailing || !supplier?.email}
            title={!supplier?.email ? "Supplier has no email on file" : "Email PDF statement to supplier"}
            data-testid="supplier-statement-email-btn"
          >
            {emailing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
            {emailing ? "Sending..." : "Email PDF"}
          </Button>
        </div>
      </div>

      {/* Note: the printed statement is rendered via a dedicated print host
          built inside `handlePrint` — no inline print-only header here. */}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" id="supplier-statement-table">
          <div className="max-h-[500px] overflow-y-auto print-no-scroll">
            <Table>
              <TableHeader className="bg-gray-50 sticky top-0">
                <TableRow>
                  <TableHead className="w-28">Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="w-32">Type</TableHead>
                  <TableHead className="text-right w-28">Debit</TableHead>
                  <TableHead className="text-right w-28">Credit</TableHead>
                  <TableHead className="text-right w-32">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No transactions for this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((txn) => (
                      <TableRow key={txn.id}>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          {formatRowDate(txn.date)}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div>{txn.reference_label}</div>
                          {txn.description && txn.description.trim() && txn.description.trim() !== txn.reference_label && (
                            <div className="text-xs text-gray-500 mt-0.5 leading-tight whitespace-pre-wrap break-words max-w-md">
                              {txn.description.replace(/^GRV\s+GRV-\d+\s*-\s*/i, "").trim()}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${typeBadgeClass(txn.type_label)}`}>
                            {txn.type_label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {txn.debit > 0 ? `R ${txn.debit.toFixed(2)}` : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-700">
                          {txn.credit > 0 ? `-R ${txn.credit.toFixed(2)}` : '—'}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${txn.running_balance < 0 ? 'text-green-700' : ''}`}>
                          R {txn.running_balance.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Brought-forward (opening) row */}
                    <TableRow className="bg-gray-50">
                      <TableCell className="font-mono text-xs">
                        {formatRowDate(dateRange.start)}
                      </TableCell>
                      <TableCell className="italic text-gray-600">Brought forward</TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700">B/Forward</span>
                      </TableCell>
                      <TableCell className="text-right font-mono">—</TableCell>
                      <TableCell className="text-right font-mono">—</TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${openingBalance < 0 ? 'text-green-700' : ''}`}>
                        R {(openingBalance || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Footer totals */}
          {rows.length > 0 && (
            <div className="bg-gray-50 border-t px-4 py-3 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-500 text-xs">Total Debit</div>
                <div className="font-mono font-semibold">R {totalDebit.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Total Credit</div>
                <div className="font-mono font-semibold text-green-700">-R {totalCredit.toFixed(2)}</div>
              </div>
              <div className="text-right">
                <div className="text-gray-500 text-xs">Closing Balance</div>
                <div className={`font-mono font-bold text-lg ${closingBalance < 0 ? 'text-green-700' : ''}`}>
                  R {closingBalance.toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Format YYYY-MM-DD → "14 May 2026"
const formatRowDate = (s) => {
  if (!s || s.length < 10) return s || '—';
  const d = new Date(s + (s.includes('T') ? '' : 'T00:00:00'));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const typeBadgeClass = (label) => {
  if (label === "Debit Journal") return "bg-red-50 text-red-700 border border-red-200";
  if (label === "Credit Journal") return "bg-green-50 text-green-700 border border-green-200";
  if (label === "B/Forward") return "bg-gray-200 text-gray-700";
  if (label === "COD") return "bg-amber-50 text-amber-700 border border-amber-200";
  if (label === "Account Invoice") return "bg-blue-50 text-blue-700 border border-blue-200";
  return "bg-blue-50 text-blue-700 border border-blue-200";
};

// Journal Tab Component
const JournalTab = ({ supplier, onJournalSaved }) => {
  const [formData, setFormData] = useState({
    journal_type: "PAYMENT",
    date: new Date().toISOString().split('T')[0],
    reference: "",
    notes: "",
    amount: "",
    payment_method: "EFT"
  });
  const [saving, setSaving] = useState(false);
  const [showBulkWipe, setShowBulkWipe] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);

  const handleWipeAllJournals = async () => {
    if (!supplier?.id) return;
    if (wipeConfirmText.trim().toUpperCase() !== "DELETE") {
      toast.error("Type DELETE to confirm.");
      return;
    }
    setWiping(true);
    try {
      const r = await axios.post(
        `${API}/suppliers/${supplier.id}/journals/delete-all?confirm=true`,
        {},
        { timeout: 60000 }
      );
      toast.success(`Removed ${r.data?.deleted ?? 0} journal row(s) from ${supplier.name}`);
      setShowBulkWipe(false);
      setWipeConfirmText("");
      onJournalSaved?.();
    } catch (err) {
      const raw = err.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : err.message || "Bulk delete failed");
    } finally {
      setWiping(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProcess = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSaving(true);
    // One idempotency key survives every retry — the backend upserts on
    // this key so a proxy 504/524/500 retry can never insert a duplicate
    // journal (this is what caused the 6× SJ-000559..564 bug).
    const idempotency_key = (crypto?.randomUUID?.() || `jrnl-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    try {
      await saveWithRetry(() => axios.post(`${API}/suppliers/${supplier.id}/journal`, {
        journal_type: formData.journal_type,
        date: formData.date,
        amount: parseFloat(formData.amount),
        description: formData.notes,
        reference: formData.reference,
        payment_method: formData.payment_method,
        idempotency_key
      }));
      toast.success(`${formData.journal_type} of R ${parseFloat(formData.amount).toFixed(2)} recorded`);
      setFormData({
        journal_type: "PAYMENT",
        date: new Date().toISOString().split('T')[0],
        reference: "",
        notes: "",
        amount: "",
        payment_method: "EFT"
      });
      onJournalSaved?.();
    } catch (error) {
      console.error("Error saving journal:", error);
      toast.error(errorMessage(error, "Failed to save journal"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Account Summary */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-blue-50 rounded-lg">
        <div>
          <Label className="text-xs text-gray-500">Account No</Label>
          <div className="font-semibold">{supplier?.account_number || 'N/A'}</div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Credit Limit</Label>
          <div className="font-semibold">R {(supplier?.credit_limit || 0).toFixed(2)}</div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Balance Owing</Label>
          <div className="font-semibold text-red-600">R {(supplier?.balance_owing || 0).toFixed(2)}</div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Unallocated</Label>
          <div className="font-semibold text-green-600">R {(supplier?.unallocated_payments || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Aging Row */}
      <div className={`grid ${supplier?.payment_terms === '30 Days' ? 'grid-cols-6' : 'grid-cols-5'} gap-2 text-center text-sm`}>
        {supplier?.payment_terms === '30 Days' && (
          <div className="p-2 bg-gray-50 rounded">
            <div className="text-xs text-gray-600">Not Yet Due</div>
            <div className="font-bold">R {(supplier?.aging?.not_yet_due || 0).toFixed(2)}</div>
          </div>
        )}
        <div className="p-2 bg-green-50 rounded">
          <div className="text-xs text-green-600">{supplier?.payment_terms === '30 Days' ? 'Payable Now' : 'Current'}</div>
          <div className="font-bold">R {(supplier?.aging?.current || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-yellow-50 rounded">
          <div className="text-xs text-yellow-600">30 Days</div>
          <div className="font-bold">R {(supplier?.aging?.days_30 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-orange-50 rounded">
          <div className="text-xs text-orange-600">60 Days</div>
          <div className="font-bold">R {(supplier?.aging?.days_60 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-red-50 rounded">
          <div className="text-xs text-red-500">90 Days</div>
          <div className="font-bold">R {(supplier?.aging?.days_90 || 0).toFixed(2)}</div>
        </div>
        {supplier?.payment_terms !== '30 Days' && (
          <div className="p-2 bg-red-100 rounded">
            <div className="text-xs text-red-600">120+ Days</div>
            <div className="font-bold">R {(supplier?.aging?.days_120 || supplier?.aging?.over_120 || 0).toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Journal Form */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Journal Type</Label>
            <Select value={formData.journal_type} onValueChange={(v) => handleChange('journal_type', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAYMENT">PAYMENT</SelectItem>
                <SelectItem value="CREDIT">CREDIT</SelectItem>
                <SelectItem value="DEBIT">DEBIT</SelectItem>
                <SelectItem value="DISCOUNT">DISCOUNT</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date</Label>
            <Input 
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Reference</Label>
            <Input 
              value={formData.reference}
              onChange={(e) => handleChange('reference', e.target.value)}
              placeholder="e.g., EFT123456"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Amount</Label>
            <Input 
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              placeholder="0.00"
              className="mt-1 text-lg font-bold"
              step="0.01"
            />
          </div>
        </div>

        <div>
          <Label>Notes</Label>
          <Textarea 
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional notes..."
            className="mt-1"
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => setFormData({
            journal_type: "PAYMENT",
            date: new Date().toISOString().split('T')[0],
            reference: "",
            notes: "",
            amount: "",
            payment_method: "EFT"
          })}>
            Cancel
          </Button>
          <Button onClick={handleProcess} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Process
          </Button>
        </div>
      </div>

      {/* Danger zone: wipe every journal for this supplier. Guarded by a
          typed-DELETE confirmation so it can't be triggered by accident. */}
      <div className="border border-red-300 bg-red-50 rounded-lg p-4" data-testid="journal-danger-zone">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <div className="font-semibold text-red-800">Danger zone — delete ALL journal rows</div>
            <div className="text-red-700/80 text-xs mt-1 max-w-xl">
              Removes every journal entry (payments, credits, debit adjustments) for <b>{supplier?.name}</b>.
              GRVs and payment records are NOT touched. Use this to wipe phantom rows caused by earlier retry
              storms — the account balance will recompute automatically.
            </div>
          </div>
          {!showBulkWipe ? (
            <Button
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-100"
              onClick={() => setShowBulkWipe(true)}
              data-testid="journal-wipe-all-btn"
            >
              Delete all journals…
            </Button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={wipeConfirmText}
                onChange={(e) => setWipeConfirmText(e.target.value)}
                placeholder='type DELETE to confirm'
                className="w-40 h-9 border-red-300"
                data-testid="journal-wipe-confirm-input"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowBulkWipe(false); setWipeConfirmText(""); }}
                data-testid="journal-wipe-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleWipeAllJournals}
                disabled={wiping || wipeConfirmText.trim().toUpperCase() !== "DELETE"}
                data-testid="journal-wipe-confirm-btn"
              >
                {wiping ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <X className="h-4 w-4 mr-1" />
                )}
                Delete all
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Reconcile Tab Component
const ReconcileTab = ({ supplier, onReconciled }) => {
  const [reconcileData, setReconcileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [availableForAllocation, setAvailableForAllocation] = useState(0);
  const [repairingBalances, setRepairingBalances] = useState(false);
  const [lastBackupId, setLastBackupId] = useState("");
  const [sendingReconEmail, setSendingReconEmail] = useState(false);

  // Handle emailing payment reconciliation report
  const handleEmailPaymentRecon = async () => {
    if (!supplier) return;
    
    // Always allow user to edit/enter email address
    const defaultEmail = supplier.email || "";
    const emailTo = prompt("Enter email address to send payment reconciliation report:", defaultEmail);
    
    if (!emailTo || !emailTo.trim()) return;
    
    setSendingReconEmail(true);
    try {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const startDate = threeMonthsAgo.toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];
      
      const res = await axios.post(`${API}/suppliers/${supplier.id}/email-payment-reconciliation`, {
        email_to: emailTo.trim(),
        start_date: startDate,
        end_date: endDate
      });
      toast.success(`Report sent to ${emailTo} (${res.data.payments_count} payments, R${res.data.total_amount?.toFixed(2)})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingReconEmail(false);
    }
  };

  useEffect(() => {
    fetchReconcileData();
  }, [supplier?.id]);

  const fetchReconcileData = async () => {
    if (!supplier?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers/${supplier.id}/reconcile`);
      setReconcileData(response.data);
      setAvailableForAllocation(response.data?.unallocated_payments || 0);
    } catch (error) {
      console.error("Error fetching reconcile data:", error);
      toast.error("Failed to load reconciliation data");
    } finally {
      setLoading(false);
    }
  };

  const handleDoubleClick = (invoice) => {
    if (invoice.outstanding <= 0) return; // Already fully paid
    setSelectedInvoice(invoice);
    // Pre-fill with the smaller of: outstanding amount or available funds
    const maxAlloc = Math.min(invoice.outstanding, availableForAllocation);
    setAllocateAmount(maxAlloc > 0 ? maxAlloc.toFixed(2) : "");
  };

  const handleAllocate = async () => {
    if (!selectedInvoice || !allocateAmount) return;
    
    const amount = parseFloat(allocateAmount);
    if (amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (amount > availableForAllocation) {
      toast.error("Amount exceeds available funds");
      return;
    }
    if (amount > selectedInvoice.outstanding) {
      toast.error("Amount exceeds invoice outstanding");
      return;
    }

    setSaving(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/suppliers/${supplier.id}/reconcile`, {
        assignments: [{
          grv_id: selectedInvoice.grv_id,
          invoice_number: selectedInvoice.invoice_number,
          amount: amount
        }]
      }));
      toast.success(`Allocated R ${amount.toFixed(2)} to invoice ${selectedInvoice.invoice_number}`);
      setSelectedInvoice(null);
      setAllocateAmount("");
      fetchReconcileData();
      onReconciled?.();
    } catch (error) {
      console.error("Error allocating:", error);
      toast.error(errorMessage(error, "Failed to allocate"));
    } finally {
      setSaving(false);
    }
  };

  const handleAutoAllocate = async () => {
    if (availableForAllocation <= 0) {
      toast.error("No funds available for allocation");
      return;
    }

    setSaving(true);
    try {
      // Auto-allocate to oldest invoices first
      const invoices = reconcileData?.invoices?.filter(inv => inv.outstanding > 0)
        .sort((a, b) => (a.date || '').localeCompare(b.date || '')) || [];
      
      let remaining = availableForAllocation;
      const assignments = [];

      for (const inv of invoices) {
        if (remaining <= 0) break;
        const allocAmount = Math.min(inv.outstanding, remaining);
        assignments.push({
          grv_id: inv.grv_id,
          invoice_number: inv.invoice_number,
          amount: allocAmount
        });
        remaining -= allocAmount;
      }

      if (assignments.length === 0) {
        toast.info("No invoices to allocate to");
        return;
      }

      await saveWithRetry(() => axios.post(`${API}/suppliers/${supplier.id}/reconcile`, { assignments }));
      toast.success(`Auto-allocated R ${(availableForAllocation - remaining).toFixed(2)} to ${assignments.length} invoice(s)`);
      fetchReconcileData();
      onReconciled?.();
    } catch (error) {
      console.error("Error auto-allocating:", error);
      toast.error(errorMessage(error, "Failed to auto-allocate"));
    } finally {
      setSaving(false);
    }
  };

  const handleRepairBalances = async () => {
    const confirmRepair = window.confirm(
      "Run BROAD supplier balance repair?\n\n" +
      "This will scan suppliers, rebuild mismatched invoice allocations, and create a full backup for rollback."
    );
    if (!confirmRepair) return;

    setRepairingBalances(true);
    try {
      const response = await axios.post(`${API}/suppliers/balance-repair/rebuild-allocations`, {
        mode: "broad",
        dry_run: false,
        note: `Triggered from supplier ${supplier?.name || supplier?.id}`
      });
      const backupId = response.data?.backup_id || "";
      if (backupId) {
        setLastBackupId(backupId);
      }
      toast.success(
        `Balance repair complete. Fixed ${response.data?.fixed_suppliers?.length || 0} supplier(s).` +
        (backupId ? ` Backup: ${backupId}` : "")
      );
      fetchReconcileData();
      onReconciled?.();
    } catch (error) {
      console.error("Error running supplier balance repair:", error);
      toast.error(error.response?.data?.detail || "Failed to run supplier balance repair");
    } finally {
      setRepairingBalances(false);
    }
  };

  const handleRestoreBalanceBackup = async () => {
    if (!lastBackupId) {
      toast.error("No backup id available to restore");
      return;
    }

    const confirmRestore = window.confirm(
      `Restore backup ${lastBackupId}?\n\nThis will revert supplier payments/journals to the pre-repair snapshot.`
    );
    if (!confirmRestore) return;

    setRepairingBalances(true);
    try {
      await axios.post(`${API}/suppliers/balance-repair/restore/${lastBackupId}`);
      toast.success(`Backup restored: ${lastBackupId}`);
      fetchReconcileData();
      onReconciled?.();
    } catch (error) {
      console.error("Error restoring supplier balance backup:", error);
      toast.error(error.response?.data?.detail || "Failed to restore backup");
    } finally {
      setRepairingBalances(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Debug function for supplier balance
  const handleDebugBalance = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplier.id}/debug-balance`);
      const data = response.data;
      
      // Create a readable alert with the improved debug info
      const debugInfo = `
SUPPLIER BALANCE DEBUG - ${data.supplier_name}
===============================================

CORRECTED BALANCE: R ${data.calculated_balance?.toFixed(2)}

RAW BREAKDOWN (before deduplication):
- Non-COD GRVs (${data.counts?.non_cod_grvs}): R ${data.raw_breakdown?.total_non_cod_grvs?.toFixed(2)}
- COD GRVs (${data.counts?.cod_grvs}): R ${data.raw_breakdown?.total_cod_grvs?.toFixed(2)} (excluded)
- Journal Debits: R ${data.raw_breakdown?.journal_debits?.toFixed(2)}
- Opening Balance: R ${data.opening_balance?.toFixed(2)}
- Payments (${data.counts?.payments}): R ${data.raw_breakdown?.total_payments?.toFixed(2)}
- Journal Credits (${data.counts?.credit_journals || 0}): R ${data.raw_breakdown?.journal_credits?.toFixed(2)}
- RAW Balance: R ${data.raw_breakdown?.raw_balance?.toFixed(2)}

CORRECTED BREAKDOWN (after deduplication):
- Duplicate Credits Removed: R ${data.corrected_breakdown?.duplicate_credits_removed?.toFixed(2)}
- Deduplicated Journal Credits: R ${data.corrected_breakdown?.journal_credits_deduplicated?.toFixed(2)}
- Total Debit: R ${data.corrected_breakdown?.total_debit?.toFixed(2)}
- Total Credit: R ${data.corrected_breakdown?.total_credit?.toFixed(2)}
- CORRECTED Balance: R ${data.corrected_breakdown?.corrected_balance?.toFixed(2)}

${data.excess_credits_analysis?.has_excess_credits ? `⚠️ EXCESS CREDITS DETECTED:\n${data.excess_credits_analysis.explanation}\n` : ''}
${data.diagnosis?.recommendation || 'No issues detected.'}

JOURNAL ENTRIES (${data.journals_detail?.length || 0} total):
${(data.journals_detail || []).map(j => `- ${j.ref}: ${j.type} R${j.amount?.toFixed(2)} - ${j.description || 'No description'}`).join('\n')}
      `;
      
      alert(debugInfo);
      console.log("Full debug data:", data);
      
      // If excess credits detected, offer to view and manage journals
      if (data.excess_credits_analysis?.has_excess_credits) {
        const manageJournals = window.confirm(
          `⚠️ EXCESS CREDITS DETECTED!\n\n` +
          `Credits: R${data.excess_credits_analysis.total_credits.toFixed(2)}\n` +
          `Debts: R${data.excess_credits_analysis.total_owed.toFixed(2)}\n` +
          `Excess: R${data.excess_credits_analysis.excess_amount.toFixed(2)}\n\n` +
          `There are ${data.counts?.credit_journals || 0} credit journal entries. ` +
          `Some may be erroneous.\n\n` +
          `Would you like to view and manage journal entries?`
        );
        if (manageJournals) {
          await handleManageJournals();
        }
      }
      // If duplicates found, offer to remove them
      else if (data.diagnosis?.has_double_counting && data.diagnosis?.double_counted_amount > 0) {
        const confirmRemove = window.confirm(
          `Found ${data.counts?.duplicate_journals_found} duplicate journal entries totaling R${data.diagnosis.double_counted_amount.toFixed(2)}.\n\nWould you like to remove these duplicates to fix the balance?`
        );
        if (confirmRemove) {
          await handleRemoveDuplicates();
        }
      }
    } catch (error) {
      console.error("Error fetching debug data:", error);
      toast.error("Failed to load debug data");
    }
  };

  // Manage journal entries - view and delete
  const handleManageJournals = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplier.id}/journals`);
      const data = response.data;
      
      const journalList = data.journals.map((j, idx) => 
        `${idx + 1}. ${j.journal_ref}: ${j.journal_type} R${j.amount?.toFixed(2)} (${j.date}) - ${(j.description || 'No description').substring(0, 40)}`
      ).join('\n');
      
      const journalInfo = `
JOURNAL ENTRIES FOR ${data.supplier_name}
=========================================

Total Credits: R${data.total_credits.toFixed(2)} (${data.credit_journals_count} entries)
Total Debits: R${data.total_debits.toFixed(2)} (${data.debit_journals_count} entries)

ENTRIES:
${journalList}

To delete a journal entry, enter its number (1-${data.journals.length}), or click Cancel to exit:
      `;
      
      const selection = window.prompt(journalInfo);
      
      if (selection && !isNaN(selection)) {
        const idx = parseInt(selection) - 1;
        if (idx >= 0 && idx < data.journals.length) {
          const journalToDelete = data.journals[idx];
          const confirmDelete = window.confirm(
            `Are you sure you want to delete this journal entry?\n\n` +
            `Ref: ${journalToDelete.journal_ref}\n` +
            `Type: ${journalToDelete.journal_type}\n` +
            `Amount: R${journalToDelete.amount?.toFixed(2)}\n` +
            `Date: ${journalToDelete.date}\n` +
            `Description: ${journalToDelete.description}\n\n` +
            `This action cannot be undone.`
          );
          
          if (confirmDelete) {
            await axios.delete(`${API}/suppliers/${supplier.id}/journals/${journalToDelete.id}`);
            toast.success(`Journal ${journalToDelete.journal_ref} deleted successfully`);
            fetchReconcileData();
            
            // Offer to continue managing
            const continueManaging = window.confirm("Journal deleted. Would you like to continue managing journals?");
            if (continueManaging) {
              await handleManageJournals();
            }
          }
        } else {
          toast.error("Invalid selection");
        }
      }
    } catch (error) {
      console.error("Error managing journals:", error);
      toast.error("Failed to load journal entries");
    }
  };

  // Remove duplicate journal entries
  const handleRemoveDuplicates = async () => {
    try {
      // First preview
      const previewResponse = await axios.post(`${API}/suppliers/${supplier.id}/remove-duplicate-journals?preview_only=true`);
      const preview = previewResponse.data;
      
      if (preview.duplicates_found === 0) {
        toast.info("No duplicate journal entries found.");
        return;
      }
      
      const confirmDelete = window.confirm(
        `Found ${preview.duplicates_found} duplicate journal entries totaling R${preview.duplicate_amount.toFixed(2)}.\n\nDo you want to remove them? This action cannot be undone.`
      );
      
      if (confirmDelete) {
        const deleteResponse = await axios.post(`${API}/suppliers/${supplier.id}/remove-duplicate-journals?preview_only=false`);
        toast.success(deleteResponse.data.message);
        // Refresh the data
        fetchReconcileData();
      }
    } catch (error) {
      console.error("Error removing duplicates:", error);
      toast.error("Failed to remove duplicate entries");
    }
  };

  return (
    <div className="space-y-4 p-4">
      {/* Available for Allocation Header */}
      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
        <div>
          <span className="text-sm text-gray-600">Account No: </span>
          <span className="font-semibold">{supplier?.account_number || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Available for Allocation: </span>
            <span className={`text-xl font-bold ${availableForAllocation < 0 ? 'text-red-600' : 'text-green-700'}`} data-testid="supplier-available-for-allocation-value">R {availableForAllocation.toFixed(2)}</span>
            {availableForAllocation !== 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs px-2"
                onClick={async () => {
                  if (!window.confirm(`Reset allocation balance from R ${availableForAllocation.toFixed(2)} to R 0.00?`)) return;
                  try {
                    await axios.post(`${API}/suppliers/${supplier.id}/reset-allocation-balance`);
                    toast.success("Allocation balance reset to R 0.00");
                    fetchReconcileData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to reset");
                  }
                }}
                data-testid="reset-allocation-balance-btn"
              >
                Reset to 0
              </Button>
            )}
          </div>
          {reconcileData?.invalid_allocations_total > 0 && (
            <div className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded" data-testid="supplier-invalid-allocation-warning">
              Invalid allocation refs: R {reconcileData.invalid_allocations_total.toFixed(2)}
            </div>
          )}
          <Button 
            onClick={handleDebugBalance} 
            variant="outline"
            size="sm"
            className="text-gray-600"
            data-testid="supplier-reconcile-debug-button"
          >
            🔍 Debug
          </Button>
          <Button
            onClick={handleRepairBalances}
            disabled={repairingBalances}
            variant="outline"
            size="sm"
            className="text-blue-700 border-blue-300"
            data-testid="supplier-balance-repair-button"
          >
            {repairingBalances ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Repair Balances
          </Button>
          {lastBackupId && (
            <Button
              onClick={handleRestoreBalanceBackup}
              disabled={repairingBalances}
              variant="outline"
              size="sm"
              className="text-orange-700 border-orange-300"
              data-testid="supplier-balance-restore-button"
            >
              Restore Backup
            </Button>
          )}
          <Button 
            onClick={handleAutoAllocate} 
            disabled={saving || availableForAllocation <= 0 || repairingBalances}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="supplier-auto-allocate-button"
          >
            <Zap className="h-4 w-4 mr-1" /> Auto-Allocate
          </Button>
          <Button
            onClick={handleEmailPaymentRecon}
            disabled={sendingReconEmail}
            variant="outline"
            size="sm"
            className="text-purple-700 border-purple-300 hover:bg-purple-50"
            data-testid="supplier-email-recon-button"
          >
            {sendingReconEmail ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-1" />
            )}
            Email Recon
          </Button>
        </div>
      </div>
      {lastBackupId && (
        <div className="text-xs text-gray-600" data-testid="supplier-balance-repair-backup-id">
          Last backup id: <span className="font-mono">{lastBackupId}</span>
        </div>
      )}

      {/* Instructions */}
      <div className="text-sm text-gray-500 italic">
        Double-click on a <span className="text-black font-semibold">BLACK</span> invoice to allocate funds. 
        <span className="text-orange-600 font-semibold"> ORANGE</span> invoices are fully reconciled.
      </div>

      {/* Invoice Table */}
      <div className="border rounded-lg max-h-[400px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-gray-100">
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Inv No</TableHead>
              <TableHead className="text-right">Inv Total</TableHead>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Amount Paid</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reconcileData?.all_invoices?.map((inv, idx) => {
              const isFullyPaid = inv.outstanding <= 0;
              return (
                <TableRow 
                  key={idx}
                  className={`cursor-pointer ${isFullyPaid ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`}
                  onDoubleClick={() => handleDoubleClick(inv)}
                >
                  <TableCell className={`font-medium ${isFullyPaid ? 'text-orange-600' : 'text-black'}`}>
                    INVOICE
                  </TableCell>
                  <TableCell className={isFullyPaid ? 'text-orange-600' : 'text-black'}>
                    {inv.date}
                  </TableCell>
                  <TableCell className={isFullyPaid ? 'text-orange-600' : 'text-black font-semibold'}>
                    {inv.invoice_number}
                  </TableCell>
                  <TableCell className={`text-right ${isFullyPaid ? 'text-orange-600' : 'text-black'}`}>
                    R {inv.invoice_total?.toFixed(2)}
                  </TableCell>
                  <TableCell className={isFullyPaid ? 'text-orange-600' : 'text-black'}>
                    {inv.period}
                  </TableCell>
                  <TableCell className={`text-right ${isFullyPaid ? 'text-orange-600' : 'text-green-600'}`}>
                    R {inv.amount_paid?.toFixed(2)}
                  </TableCell>
                  <TableCell className={`text-right font-bold ${isFullyPaid ? 'text-orange-600' : 'text-red-600'}`}>
                    R {inv.outstanding?.toFixed(2)}
                  </TableCell>
                </TableRow>
              );
            })}
            {(!reconcileData?.all_invoices || reconcileData.all_invoices.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No invoices found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Aging Summary */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center text-sm" data-testid="reconcile-aging-summary">
        <div className="p-2 bg-gray-50 rounded">
          <div className="text-xs text-gray-600">Not Yet Due</div>
          <div className="font-bold" data-testid="reconcile-aging-not-yet-due">R {(reconcileData?.aging?.not_yet_due || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-green-50 rounded">
          <div className="text-xs text-green-600">{supplier?.payment_terms === '30 Days' ? 'Payable This Month' : 'Current'}</div>
          <div className="font-bold" data-testid="reconcile-aging-current">R {(reconcileData?.aging?.current || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-yellow-50 rounded">
          <div className="text-xs text-yellow-600">30 Days</div>
          <div className="font-bold">R {(reconcileData?.aging?.days_30 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-orange-50 rounded">
          <div className="text-xs text-orange-600">60 Days</div>
          <div className="font-bold">R {(reconcileData?.aging?.days_60 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-red-50 rounded">
          <div className="text-xs text-red-500">90 Days</div>
          <div className="font-bold">R {(reconcileData?.aging?.days_90 || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-red-100 rounded">
          <div className="text-xs text-red-600">120+ Days</div>
          <div className="font-bold">R {((reconcileData?.aging?.days_120 || 0) + (reconcileData?.aging?.over_120 || 0)).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-blue-100 rounded border-2 border-blue-300">
          <div className="text-xs text-blue-600">Gross Outstanding</div>
          <div className="font-bold text-blue-700" data-testid="supplier-gross-outstanding-value">R {(reconcileData?.total_outstanding || 0).toFixed(2)}</div>
        </div>
        <div className="p-2 bg-indigo-100 rounded border-2 border-indigo-300">
          <div className="text-xs text-indigo-700">Net Balance Owing</div>
          <div className="font-bold text-indigo-700" data-testid="supplier-net-balance-owing-value">R {(reconcileData?.balance_owing || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Allocation Dialog */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate to Invoice {selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Invoice Total:</span>
                <span className="font-bold ml-2">R {selectedInvoice?.invoice_total?.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-500">Already Paid:</span>
                <span className="font-bold ml-2 text-green-600">R {selectedInvoice?.amount_paid?.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-500">Outstanding:</span>
                <span className="font-bold ml-2 text-red-600">R {selectedInvoice?.outstanding?.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-500">Available:</span>
                <span className="font-bold ml-2 text-blue-600">R {availableForAllocation.toFixed(2)}</span>
              </div>
            </div>
            <div>
              <Label>Amount to Allocate</Label>
              <Input 
                type="number"
                value={allocateAmount}
                onChange={(e) => setAllocateAmount(e.target.value)}
                className="mt-1 text-lg font-bold"
                step="0.01"
                max={Math.min(selectedInvoice?.outstanding || 0, availableForAllocation)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedInvoice(null)}>Cancel</Button>
              <Button onClick={handleAllocate} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Allocate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Modal Component
export const SupplierDetailModal = ({ open, onClose, supplier: initialSupplier, onUpdate }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [supplier, setSupplier] = useState(initialSupplier);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialSupplier?.id) {
      fetchSupplierDetails();
    }
  }, [initialSupplier?.id]);

  const fetchSupplierDetails = async () => {
    try {
      // Fetch full supplier details with financial info
      const [supplierRes, financialRes, reconcileRes] = await Promise.all([
        axios.get(`${API}/suppliers/${initialSupplier.id}`),
        axios.get(`${API}/suppliers/${initialSupplier.id}/financial`).catch(() => ({ data: {} })),
        axios.get(`${API}/suppliers/${initialSupplier.id}/reconcile`).catch(() => ({ data: {} }))
      ]);
      
      setSupplier({
        ...supplierRes.data,
        aging: financialRes.data?.aging || reconcileRes.data?.aging || {},
        balance_owing: reconcileRes.data?.balance_owing ?? reconcileRes.data?.total_outstanding ?? financialRes.data?.summary?.total_outstanding ?? 0,
        unallocated_payments: reconcileRes.data?.unallocated_payments || 0,
        last_purchase_date: supplierRes.data?.last_purchase_date || 'N/A'
      });
    } catch (error) {
      console.error("Error fetching supplier details:", error);
    }
  };

  const handleSaveProfile = async (formData) => {
    setSaving(true);
    try {
      await saveWithRetry(() => axios.put(`${API}/suppliers/${supplier.id}`, formData));
      toast.success("Supplier updated successfully");
      fetchSupplierDetails();
      onUpdate?.();
    } catch (error) {
      console.error("Error saving supplier:", error);
      toast.error(errorMessage(error, "Failed to save supplier"));
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchSupplierDetails();
  };

  const handleClearUnallocated = async () => {
    if (!supplier?.id) return;
    
    const unallocatedAmount = supplier?.unallocated_payments || 0;
    if (unallocatedAmount <= 0) {
      toast.info("No unallocated payments to clear");
      return;
    }
    
    const confirmClear = window.confirm(
      `Are you sure you want to clear unallocated payments for ${supplier.name}?\n\n` +
      `Current unallocated amount: R ${unallocatedAmount.toFixed(2)}\n\n` +
      `This will apply the amount to reduce the opening balance.`
    );
    
    if (!confirmClear) return;
    
    try {
      const response = await axios.post(`${API}/suppliers/${supplier.id}/data-cleanup/clear-unallocated`, {
        action: 'apply_to_opening'
      });
      toast.success(response.data?.message || "Unallocated payments cleared successfully");
      fetchSupplierDetails();
      onUpdate?.();
    } catch (error) {
      console.error("Error clearing unallocated payments:", error);
      toast.error(error.response?.data?.detail || "Failed to clear unallocated payments");
    }
  };

  if (!supplier) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Building2 className="h-6 w-6 text-blue-600" />
            {supplier.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="profile" className="flex items-center gap-1">
              <User className="h-4 w-4" /> Profile
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex items-center gap-1">
              <FileText className="h-4 w-4" /> Transactions
            </TabsTrigger>
            <TabsTrigger value="journal" className="flex items-center gap-1">
              <Banknote className="h-4 w-4" /> Journal
            </TabsTrigger>
            <TabsTrigger value="reconcile" className="flex items-center gap-1">
              <CreditCard className="h-4 w-4" /> Reconcile
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="profile" className="m-0">
              <ProfileTab supplier={supplier} onSave={handleSaveProfile} saving={saving} onReset={handleRefresh} onClearUnallocated={handleClearUnallocated} onRefresh={handleRefresh} />
            </TabsContent>
            <TabsContent value="transactions" className="m-0">
              <TransactionsTab supplier={supplier} supplierId={supplier.id} />
            </TabsContent>
            <TabsContent value="journal" className="m-0">
              <JournalTab supplier={supplier} onJournalSaved={handleRefresh} />
            </TabsContent>
            <TabsContent value="reconcile" className="m-0">
              <ReconcileTab supplier={supplier} onReconciled={handleRefresh} />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierDetailModal;
